/*
 * Complete the Look (snippets/complete-the-look.liquid)
 *
 * Loads the viewed product's complementary products through Shopify's
 * recommendations endpoint, rendered by sections/complete-the-look-recs.liquid,
 * and adds a single accessory variant to the cart. The main product's size
 * picker and Add to Bag are never touched.
 *
 * Cart updates use the same events as the product page's Add to Bag:
 *   variant:add + cart:change  -> Impact cart drawer and cart count
 *   cart:updated               -> Gomila mega nav cart badge
 */
(function () {
  if (window.customElements.get('complete-the-look')) return;

  var RESULTS_SECTION = 'complete-the-look-recs';
  // Ask for more than the display limit so sold-out picks can be skipped.
  var FETCH_LIMIT = 10;

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function dispatch(name, detail) {
    document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  class CompleteTheLook extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;
      this._list = this.querySelector('[data-ctl-list]');

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this._load();
    }

    get _limit() {
      var limit = parseInt(this.getAttribute('limit'), 10);
      return limit > 0 ? limit : 2;
    }

    _load() {
      var url = shopRoot() + 'recommendations/products'
        + '?product_id=' + encodeURIComponent(this.getAttribute('product-id'))
        + '&limit=' + FETCH_LIMIT
        + '&intent=complementary'
        + '&section_id=' + RESULTS_SECTION;

      fetch(url, { priority: 'low' })
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = Array.prototype.slice.call(doc.querySelectorAll('[data-ctl-item]'), 0, this._limit);
          if (!items.length) return this._showEmpty();

          this._list.replaceChildren.apply(this._list, items.map(function (item) {
            return document.importNode(item, true);
          }));
          this.hidden = false;
        }.bind(this))
        .catch(function (error) {
          console.warn('[complete-the-look]', error);
          this._showEmpty();
        }.bind(this));
    }

    _showEmpty() {
      // Hidden on the storefront; in the theme editor explain why it's empty.
      var note = this.querySelector('[data-ctl-editor-note]');
      if (note) {
        note.hidden = false;
        this.hidden = false;
      } else {
        this.hidden = true;
      }
    }

    _onChange(event) {
      var select = event.target.closest('select[data-ctl-variant]');
      if (!select) return;

      var item = select.closest('[data-ctl-item]');
      var option = select.options[select.selectedIndex];
      var price = item.querySelector('[data-ctl-price]');
      if (option && option.dataset.price && price) price.innerHTML = option.dataset.price;

      select.removeAttribute('aria-invalid');
      this._setMessage(item, '', null);
    }

    _onClick(event) {
      var button = event.target.closest('[data-ctl-add]');
      if (!button) return;
      event.preventDefault();
      this._add(button.closest('[data-ctl-item]'), button);
    }

    _setMessage(item, text, state) {
      var message = item.querySelector('[data-ctl-msg]');
      if (!message) return;
      message.textContent = text;
      if (state) message.setAttribute('data-state', state);
      else message.removeAttribute('data-state');
    }

    _add(item, button) {
      if (button.getAttribute('aria-busy') === 'true') return;

      var input = item.querySelector('[data-ctl-variant]');
      var variantId = input ? parseInt(input.value, 10) : NaN;

      if (!variantId) {
        this._setMessage(item, this.dataset.selectMessage, 'error');
        if (input && input.tagName === 'SELECT') {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
        }
        return;
      }

      var idleLabel = button.textContent;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.textContent = this.dataset.addingLabel;
      this._setMessage(item, '', null);

      // Same bundled sections as the product page's Add to Bag, so the cart
      // drawer re-renders from the add response without an extra request.
      var sections = ['variant-added'];
      dispatch('cart:prepare-bundled-sections', { sections: sections });

      var root = shopRoot();
      var succeeded = false;

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || '');
            return data;
          });
        })
        .then(function (added) {
          return fetch(root + 'cart.js')
            .then(function (response) { return response.json(); })
            .then(function (cart) {
              cart.sections = added.sections || {};
              dispatch('variant:add', { items: added.items || [added], cart: cart });
              dispatch('cart:change', { baseEvent: 'variant:add', cart: cart });
              dispatch('cart:updated', { cart: cart });
            })
            .catch(function () {
              // The item is in the cart; let the drawer and count reload themselves.
              dispatch('cart:refresh', {});
            });
        })
        .then(function () {
          succeeded = true;
          button.textContent = this.dataset.addedLabel;
          this._setMessage(item, this.dataset.addedMessage, 'success');
        }.bind(this))
        .catch(function (error) {
          // Shopify's description explains stock limits, e.g. "All 1 ... are in your cart."
          this._setMessage(item, (error && error.message) || this.dataset.errorMessage, 'error');
        }.bind(this))
        .finally(function () {
          var reset = function () {
            button.textContent = idleLabel;
            button.disabled = false;
            button.removeAttribute('aria-busy');
          };
          if (succeeded) setTimeout(reset, 2000);
          else reset();
        });
    }
  }

  window.customElements.define('complete-the-look', CompleteTheLook);
})();
